import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowLeft, Search, Shuffle, X } from 'lucide-react';
import { Input } from '../components/ui/Input';
import { MODULES, type ModuleId } from '../content';
import { DjayIdentity } from '../mascot';
import { playSoundIntent } from '../utils/sound/soundIntent';
import { hapticManager } from '../utils/hapticManager';
import { tapSpring } from '../components/shared/motionPresets';
import { MODE_THEME } from './shared/modeTheme';
import { ModuleCard } from './familyPicker/ModuleCard';
import { FamilyCard } from './familyPicker/FamilyCard';
import { QuickStartCard } from './familyPicker/QuickStartCard';
import { PickerEmptyState } from './familyPicker/PickerEmptyState';
import { useFamilyPickerData } from './familyPicker/useFamilyPickerData';
import type { FamilyPickerMode } from './familyPicker/types';
export type { FamilyPickerMode };
interface FamilyPickerProps {
  onClose: () => void;
  onSelect: (familyId: string) => void;
  mode: FamilyPickerMode;
}
const MODE_CONFIG = {
  sprint: {
    message: 'Kies een onderwerp om te trainen! 🎯',
    emotion: 'pointing' as const
  },
  free: {
    message: 'Kies wat je wilt oefenen — helemaal vrij! 🎲',
    emotion: 'happy' as const
  },
  challenge: {
    message: 'Kies je uitdaging! Dit wordt spannend 💪',
    emotion: 'excited' as const
  }
} as const;

/**
 * FamilyPicker — twee stappen: eerst een vak, dan een onderwerp.
 *
 * Dit bestand was 689 regels en deed vier dingen tegelijk: de selectielogica,
 * de twee stappen, en drie kaartvarianten met elk hun eigen voortgangsbalk. Die
 * balken waren onderling gaan afwijken — drie van de vier misten de
 * toegankelijkheidsattributen die de vierde had. Dat is de voorspelbare uitkomst
 * van kopieën in één groot bestand: een correctie landt op één plek.
 *
 * Nu: de data in `familyPicker/useFamilyPickerData.ts`, de kaarten in eigen
 * bestanden, en hier alleen de opbouw van het scherm.
 */
export function FamilyPicker({
  onClose,
  onSelect,
  mode
}: FamilyPickerProps) {
  const [selectedModule, setSelectedModule] = useState<ModuleId | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const config = MODE_CONFIG[mode];
  const theme = MODE_THEME[mode];
  const {
    allFamilies,
    moduleAggregates,
    filteredFamilies,
    hasPracticedFamilies,
    hasChallengeReadyFamilies,
    quickStart,
    showQuickStartInModule,
    learnerGroup
  } = useFamilyPickerData(mode, selectedModule, searchQuery);
  const goBack = () => {
    if (selectedModule) {
      setSelectedModule(null);
      setSearchQuery('');
    } else {
      onClose();
    }
  };
  const stepTransition = {
    duration: 0.2,
    ease: [0.23, 1, 0.32, 1] as const
  };
  return <div className="bg-slate-50 min-h-screen pb-8" data-id="element-2938" data-mp="2ogc8yjazqkad">
      {/* ── Header ── */}
      <div className={`bg-gradient-to-br ${theme.gradient} relative overflow-hidden`} data-id="element-2939" data-mp="2oe8706wnjvcq">
        
        <div className="absolute top-0 right-0 w-48 h-48 bg-white/10 rounded-full blur-3xl -translate-y-1/2 translate-x-1/4" data-id="element-2940" data-mp="2ji11ugc2ynvu" />
        <div className="absolute bottom-0 left-0 w-32 h-32 bg-white/10 rounded-full blur-2xl translate-y-1/4 -translate-x-1/4" data-id="element-2941" data-mp="2vztv84fmdoam" />

        <div className="relative z-10 px-6 sm:px-8 md:px-10 pt-6 md:pt-10 pb-6 max-w-6xl mx-auto" data-id="element-2942" data-mp="30qacjzmk9u29">
          {/* Tikdoel op 44px: dit is de enige uitweg van dit scherm, en het was
               een 32px cirkel met een tekstlabel ernaast. */}
          <button onClick={goBack} className="inline-flex items-center gap-2 min-h-11 pr-3 text-white/80 hover:text-white font-bold mb-2 transition-colors text-sm" data-id="element-2943" data-mp="291gxi57wamu9">
            
            <div className="w-11 h-11 rounded-full bg-white/20 backdrop-blur-sm flex items-center justify-center" data-id="element-2944" data-mp="2hyhh7yk0rwrd">
              <ArrowLeft className="w-4 h-4" data-id="element-2945" data-mp="2mx2hfxz9ugft" />
            </div>
            {selectedModule ? 'Terug naar vakken' : 'Terug naar Oefenlab'}
          </button>

          <div className="flex items-center gap-4" data-id="element-2946" data-mp="284xsuo96qcv">
            {/* Djay zat in een eeuwig op-en-neer wippende wrapper BOVENOP zijn
                 eigen `animate`-gedrag: twee animatielagen op één figuur, en een
                 oneindige lus op elk scherm van de kiezer. Zijn eigen animatie is
                 genoeg. */}
            <div className="flex-shrink-0" data-id="element-2947" data-mp="1k84b5j80c0ry">
              <DjayIdentity size="lg" emotion={config.emotion} animate data-id="element-2948" data-mp="1f7q2q02huote" />
            </div>

            <div className="flex-1 min-w-0" data-id="element-2949" data-mp="1u4qu22grwhrc">
              <h1 className="text-2xl sm:text-3xl font-black text-white tracking-tight leading-tight flex items-center gap-3" data-id="element-2950" data-mp="1u3fru72z7i2c">
                {theme.title}
                {/* Stond als "Experimenteel" — een woord dat een kind van acht
                     niet leest en dat niets zegt over wat het kan verwachten. */}
                {mode === 'free' && <span className="text-[10px] font-black bg-white/25 text-white px-2 py-1 rounded-lg uppercase tracking-wider" data-id="element-2951" data-mp="1yxthdsuyiuz3">
                    Nieuw
                  </span>}
              </h1>
              <div className="mt-2 bg-white/20 backdrop-blur-sm rounded-2xl px-3 py-2 inline-block max-w-full" data-id="element-2952" data-mp="27uas4uzesqub">
                <p className="text-white text-sm font-bold truncate" data-id="element-2953" data-mp="2wh0bcjvfzm5f">
                  {selectedModule ? `Kies een onderwerp voor ${MODULES.find(m => m.id === selectedModule)?.title}` : config.message}
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Was max-w-4xl binnen de max-w-6xl van de AppShell: de kiezer stond
           zichtbaar smaller dan de rest van het Oefenlab, terwijl het dezelfde
           pagina is. Nu één breedte voor het hele domein. */}
      <div className="max-w-6xl mx-auto px-6 sm:px-8 md:px-10 pt-6" data-id="element-2954" data-mp="1ytrvp5i4h7bc">
        <AnimatePresence mode="wait" data-id="element-2955" data-mp="1yrf6q8dy5wos">
          {!selectedModule ? <motion.div key="step1" initial={{
          opacity: 0,
          x: -12
        }} animate={{
          opacity: 1,
          x: 0
        }} exit={{
          opacity: 0,
          x: -12
        }} transition={stepTransition} className="space-y-6" data-id="element-2956" data-mp="23t4khvsk0ogk">
            
              {allFamilies.length === 0 ? <PickerEmptyState mode={mode} hasPracticedFamilies={hasPracticedFamilies} hasChallengeReadyFamilies={hasChallengeReadyFamilies} data-id="element-2957" data-mp="d0w2u8yqceb" /> : <>
                  {quickStart && mode !== 'free' && <QuickStartCard item={quickStart} onSelect={onSelect} isQuickStart learnerGroup={learnerGroup} data-id="element-2958" data-mp="3mrsoim5sr2xk" />}

                  <div data-id="element-2959" data-mp="3htr705o0rbo7">
                    <h2 className="text-lg font-black text-slate-800 mb-4" data-id="element-2960" data-mp="3hq3cp4ar2l8f">
                      Kies een vak
                    </h2>
                    <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4" data-id="element-2961" data-mp="eaxvdyeswwwo">
                      {moduleAggregates.map(({
                  module,
                  count,
                  avgMastery
                }, index) => <ModuleCard key={module.id} module={module} count={count} avgMastery={avgMastery} index={index} onClick={() => setSelectedModule(module.id)} data-id="element-2962" data-mp="59zq7csmmjci" data-mpi={index} />)}
                    </div>
                  </div>
                </>}
            </motion.div> : <motion.div key="step2" initial={{
          opacity: 0,
          x: 12
        }} animate={{
          opacity: 1,
          x: 0
        }} exit={{
          opacity: 0,
          x: 12
        }} transition={stepTransition} className="space-y-4" data-id="element-2963" data-mp="15iprm2kubwx9">
            
              <div className="sticky top-0 z-30 bg-slate-50/95 backdrop-blur-sm py-2 -mx-6 px-6 sm:mx-0 sm:px-0" data-id="element-2964" data-mp="1ehb5hkfdanrp">
                <Input type="text"
            /* Geen zichtbaar label (de kop erboven zegt het al), maar wel
               een toegankelijke naam: met alleen een placeholder heeft het
               veld voor een schermlezer geen naam zodra er iets in staat. */ aria-label="Zoek een onderwerp" placeholder="Zoek een onderwerp..." value={searchQuery} onChange={e => setSearchQuery(e.target.value)} icon={<Search className="w-4 h-4" data-id="element-2966" data-mp="226m8l8k6wu90" />} variant="student" size="sm" data-id="element-2965" data-mp="1o97f9sqtg2br" />
              
              </div>

              <div className="flex items-center justify-between mb-2" data-id="element-2967" data-mp="7uz5kyr59nhj">
                <p className="text-xs font-bold text-slate-400 uppercase tracking-wider" data-id="element-2968" data-mp="7v7hanbj6mr5">
                  {filteredFamilies.length} onderwerp
                  {filteredFamilies.length !== 1 ? 'en' : ''}
                </p>
                {/* "Verras me" alleen bij sprint/vrij: bij een uitdaging is de
                   keuze betekenisvol, dus die niet weggeven aan het toeval. */}
                {mode !== 'challenge' && filteredFamilies.length > 1 && <motion.button {...tapSpring} onClick={() => {
              const pick = filteredFamilies[Math.floor(Math.random() * filteredFamilies.length)];
              if (pick) {
                playSoundIntent('reward.streak');
                hapticManager.hapticLight();
                onSelect(pick.family.familyId);
              }
            }} className="inline-flex items-center gap-1.5 min-h-11 px-4 rounded-full bg-amber-500 text-white text-xs font-black uppercase tracking-wider shadow-sm hover:shadow-md transition-shadow" data-id="element-2969" data-mp="1qt3q43z395ie">
                
                    <Shuffle className="w-3.5 h-3.5" data-id="element-2970" data-mp="379zp56lippvo" />
                    Verras me
                  </motion.button>}
              </div>

              {filteredFamilies.length === 0 ? (
          /* Was een doodlopende regel tekst. Een kind dat zich vertypt,
             kan het zoekveld niet altijd zelf terugvinden onder de
             sticky rand — nu staat de uitweg in de melding zelf. */
          <div className="text-center py-12" data-id="element-2971" data-mp="p0mwi1n4uehc">
                  <p className="text-slate-500 font-medium mb-4" data-id="element-2972" data-mp="1cnv2ymwartky">
                    Niets gevonden voor &ldquo;{searchQuery}&rdquo;.
                  </p>
                  <button onClick={() => setSearchQuery('')} className="inline-flex items-center gap-1.5 min-h-11 px-4 rounded-full bg-white border-2 border-slate-200 text-sm font-bold text-slate-700 hover:border-slate-300 transition-colors" data-id="element-2973" data-mp="1grjbb7sr6t79">
                
                    <X className="w-4 h-4" data-id="element-2974" data-mp="1vm7dabyy39i4" />
                    Wis het zoekwoord
                  </button>
                </div>) : <div className="space-y-2" data-id="element-2975" data-mp="20f0ggverm9s3">
                  {showQuickStartInModule && <QuickStartCard item={filteredFamilies[0]} onSelect={onSelect} isQuickStart={false} learnerGroup={learnerGroup} data-id="element-2976" data-mp="23g4of7w02l5w" />}
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3" data-id="element-2977" data-mp="28c3i9b03kvdp">
                    {filteredFamilies.slice(showQuickStartInModule ? 1 : 0).map(({
                family,
                masteryScore,
                attempts
              }, index) => <FamilyCard key={family.familyId} family={family} masteryScore={masteryScore} attempts={attempts} index={index} learnerGroup={learnerGroup} onSelect={onSelect} data-id="element-2978" data-mp="1knk9yvh1silm" data-mpi={index} />)}
                  </div>
                </div>}
            </motion.div>}
        </AnimatePresence>
      </div>
    </div>;
}